/**
 * custom-shared-wishlist.js
 * Boots Mst Wishlist Prime (iWish) on Horizon: jQuery (required by CDN) + binders + variant sync.
 */
(function () {
  const JQUERY_SRC = 'https://code.jquery.com/jquery-3.7.1.min.js';
  const IWISH_SRC = 'https://cdn.myshopapps.com/iwish/iwishlist_v2.js';
  const HEADER_SELECTOR = '.shared-wishlist-header';
  const COUNT_SELECTOR = '.shared-wishlist-header .iWishCount';

  let countObserver = null;
  let syncingCount = false;

  /**
   * Go straight to the wishlist page on every viewport. The app's own click
   * handler slides its drawer open and the iWishPost navigation then lands on
   * the wishlist page anyway, so the drawer was a pointless flash of a loading
   * spinner before the page load — it never finished rendering anything useful.
   * Capture phase so this outruns the app's element-level handler.
   */
  function bindDirectNavigation() {
    document.addEventListener(
      'click',
      function (event) {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest("a.iWishView, a[href='/apps/iwish']");
        if (!link) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (typeof window.iWishPost === 'function') {
          window.iWishPost('/apps/iwish', {
            iwishlist: JSON.stringify(window.iWishlistmain || {}),
            cId: window.iwish_cid || '',
          });
        } else {
          window.location.assign('/apps/iwish');
        }
      },
      true
    );
  }

  bindDirectNavigation();

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      const existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', function () {
          resolve();
        });
        existing.addEventListener('error', reject);
        // A tag injected by something else may have FINISHED loading before
        // we attached these listeners (script elements expose no readyState),
        // which left this promise unsettled and boot() hung forever. Settle
        // after a grace period — every downstream consumer re-checks the
        // actual globals before using them, so resolving early is safe.
        setTimeout(resolve, 3000);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function parseCount(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Math.max(0, Math.floor(Number(value)));
    }
    return null;
  }

  function readStoredCount() {
    try {
      if (typeof Storage === 'undefined') return null;
      return parseCount(localStorage.getItem('iWishCnt'));
    } catch (error) {
      return null;
    }
  }

  function readBadgeCount() {
    const badge = document.querySelector(COUNT_SELECTOR);
    if (!badge) return null;
    return parseCount(badge.getAttribute('data-count')) ?? parseCount(badge.textContent);
  }

  function resolveWishlistCount() {
    return (
      parseCount(window.iWishCnt) ??
      readStoredCount() ??
      readBadgeCount() ??
      0
    );
  }

  function syncHeaderCount() {
    const count = resolveWishlistCount();
    const label = String(count);

    syncingCount = true;
    if (countObserver) countObserver.disconnect();

    document.querySelectorAll(HEADER_SELECTOR).forEach(function (el) {
      el.classList.toggle('is-empty', count < 1);
      el.setAttribute('data-wishlist-count', label);

      const badge = el.querySelector('.iWishCount');
      if (!badge) return;

      badge.setAttribute('data-count', label);
      if (badge.textContent !== label) {
        badge.textContent = label;
      }
    });

    if (typeof window.iWishCnt === 'number' && window.iWishCnt !== count) {
      window.iWishCnt = count;
    }

    syncingCount = false;
    observeCountBadge();
  }

  function refreshProductState() {
    if (typeof window.jQuery === 'undefined' || typeof window.checkIwish !== 'function') return;
    if (!window.iwish_pro_template) return;

    const $ = window.jQuery;
    const wrap = $('.iWishAdd:visible').parents(window.iwishWrapperClass);
    if (!wrap.length) return;
    const variantId = wrap.find(window.iWishVarSelector).val();
    if (variantId) window.checkIwish(wrap, variantId);
  }

  /* Wishlist-link navigation is handled by bindDirectNavigation() above, which
     runs in capture phase and calls stopImmediatePropagation — a delegated
     handler on document could never fire. Only the add binder lives here. */
  function bindClicks() {
    const $ = window.jQuery;

    $(document)
      .off('click.sharedWishlist', '.iWishAdd')
      .on('click.sharedWishlist', '.iWishAdd', function (event) {
        event.preventDefault();
        const $btn = $(this);
        const wrap = $btn.parents(window.iwishWrapperClass);
        const variantId = wrap.find(window.iWishVarSelector).val();
        if (typeof window.iwish_add === 'function') {
          window.iwish_add($btn, variantId);
        }
        window.setTimeout(syncHeaderCount, 50);
        window.setTimeout(syncHeaderCount, 300);
        return false;
      });
  }

  function bindHorizonVariants() {
    document.addEventListener('variant:selected', function () {
      window.setTimeout(function () {
        refreshProductState();
        syncHeaderCount();
      }, 50);
    });
    document.addEventListener('variant:update', function () {
      window.setTimeout(function () {
        refreshProductState();
        syncHeaderCount();
      }, 50);
    });
  }

  function observeCountBadge() {
    const badges = document.querySelectorAll(COUNT_SELECTOR);
    if (!badges.length || typeof MutationObserver === 'undefined') return;

    if (!countObserver) {
      countObserver = new MutationObserver(function () {
        if (syncingCount) return;
        syncHeaderCount();
      });
    }

    badges.forEach(function (badge) {
      countObserver.observe(badge, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-count'],
      });
    });
  }

  function scheduleCountResync() {
    [100, 400, 1200].forEach(function (delay) {
      window.setTimeout(syncHeaderCount, delay);
    });
  }

  async function boot() {
    try {
      if (typeof window.jQuery === 'undefined') {
        await loadScript(JQUERY_SRC);
      }
      await loadScript(IWISH_SRC);
      bindClicks();
      bindHorizonVariants();
      observeCountBadge();
      syncHeaderCount();
      scheduleCountResync();
      refreshProductState();
    } catch (error) {
      console.warn('[shared-wishlist] failed to boot Wishlist Prime', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
